import React, { useContext } from "react";
import clsx from "clsx";
import { HedAndDek } from "@bloomberg/media-ui-article";

import ErrorBoundary from "~components/ErrorBoundary";
import { GridFeatureTopContent, GridFull } from "~components/GridLayout";
import LedeImage from "~components/Lede/LedeImage";
import LedeVideo from "~components/Lede/LedeVideo";
import SectionIdentifier from "~components/SectionIdentifier";
import { Image, Video } from "~types/types";
import { CurrentStoryContext } from "~utils/currentStoryContext";

import styles from "./featureHeader.module.css";

const SPLIT_CLASSNAME = clsx(styles.media, styles.splitHolder);

export default function FeatureHeader(): JSX.Element {
    const { story } = useContext(CurrentStoryContext);
    const { hedAndDekPosition, lede, sites } = story;

    const isAbove = hedAndDekPosition === "above";
    const isLeft = hedAndDekPosition === "left";
    const isRight = hedAndDekPosition === "right";
    const isSplit = isLeft || isRight;

    const hed = (
        <GridFeatureTopContent
            className={clsx(styles.hed, {
                [styles.splitHed]: isSplit,
            })}
            data-position={hedAndDekPosition}
        >
            <SectionIdentifier
                className={styles.sectionIdentifier}
                story={story}
            />

            <HedAndDek
                headline={story.headline}
                abstract={story.abstract}
                dek={story.summaryHtml}
                className={clsx(styles.headline, {
                    [styles.headlineIsAboveMediaMargin]: isAbove,
                    [styles.headlineIsBelowMediaMargin]: !isAbove,
                })}
                unstyled
            />
        </GridFeatureTopContent>
    );

    const ledeMediaClassName = clsx(styles.ledeMedia, {
        [styles.splitLede]: isSplit,
        [styles.leftLede]: isLeft,
        [styles.rightLede]: isRight,
    });
    const ledeMedia = (
        <>
            {lede?.type === "image" && (
                <LedeImage
                    lede={lede as Image}
                    className={ledeMediaClassName}
                    isExpandable={false}
                    maxWidth={2200}
                />
            )}
            {lede?.type === "video" && (
                <ErrorBoundary fallback="failed to load Video">
                    <LedeVideo
                        lede={lede as Video}
                        className={ledeMediaClassName}
                        sites={sites}
                    />
                </ErrorBoundary>
            )}
        </>
    );

    if (isSplit && isLeft) {
        return (
            <GridFull className={SPLIT_CLASSNAME}>
                {hed}
                {ledeMedia}
            </GridFull>
        );
    }

    if (isSplit && isRight) {
        return (
            <GridFull className={SPLIT_CLASSNAME}>
                {ledeMedia}
                {hed}
            </GridFull>
        );
    }

    if (isAbove) {
        return (
            <>
                {hed}
                <GridFull
                    className={styles.media}
                    data-position={hedAndDekPosition}
                >
                    {ledeMedia}
                </GridFull>
            </>
        );
    }

    return (
        <>
            <GridFull
                className={styles.media}
                data-position={hedAndDekPosition}
            >
                {ledeMedia}
            </GridFull>
            {hed}
        </>
    );
}
